import { Steps } from 'antd';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import ReactQuill from 'react-quill-new';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../../components/Breadcrumbs';
import Button from '../../../components/button';
import FileUpload from '../../../components/FileUpload';
import Input from '../../../components/input';
import Select from '../../../components/Select';
import { apiService } from '../../../services/apiService';
import {
    fetchPartCategoriesDropdown, fetchPartsMakesDropdown,
    fetchPartsMaterialDropdown, fetchPartsModelDropdown,
    fetchPartsUnitsDropdown
} from '../../../store/Slices/dropdownSlice';
import { partCreate } from '../../../store/Slices/PartsSlice';
import { formats, modules, partInitialValues } from '../../../utils/helper';
import { partStepSchemas } from '../../../utils/Schema';
import { errorNotify, successNotify } from '../../../utils/toast';

const PartsCreate = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [getPartId, setGetPartId] = useState('')
    const [currentFormStep, setCurrentFormStep] = useState(0);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const { loading, error, partCategoriesDropdownData, partsMaterialDropdownData,
        partsMakesDropdownData, partsModelDropdownData, partsUnitsDropdownData } = useSelector((state) => state.dropdowns);

    const { partCreateLoading } = useSelector((state) => state.parts);

    useEffect(() => {
        dispatch(fetchPartCategoriesDropdown())
        dispatch(fetchPartsMaterialDropdown())
        dispatch(fetchPartsModelDropdown())
        dispatch(fetchPartsMakesDropdown())
        dispatch(fetchPartsUnitsDropdown())

        async function getPartIdData() {
            try {
                let res = await apiService.getPartId();
                setGetPartId(res?.data)
            }
            catch (e) {
                errorNotify("Failed to get Part ID")
            }
        }
        getPartIdData();
    }, [])

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const data = {
                name: values.part_name,
                company_name: values.company_name,
                part_number: values.part_number,
                sku: values.sku,
                maintenance_category_id: values.category,
                description: values.description,
                length: values.length,
                width: values.width,
                height: values.height,
                weight: values.weight,
                material: values.material,
                model: values.model,
                make: values.make,
                year: values.year,
                engine_code: values.engineCode,
                cross_reference_sku: values.crossReferenceSku,
                max_pressure: values.maxPressure,
                max_voltage: values.maxVoltage,
                max_temperature: values.maxTemperature,
                unit_id: values.unitOfMeasurement,
                stock_threshold: values.safetyStockThreshold,
                stock_count: values.availableStock,
                price: values.price,
                note: values.notes || "",
                files: uploadedFiles
                    .map((file) => file?.fileName || file?.file_path || file?.path || file)
                    .filter(Boolean)
            }

            await dispatch(partCreate(data)).unwrap();
            successNotify('Part created successfully');
            resetForm();
            setUploadedFiles([]);

            navigate('/parts');

        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={partInitialValues}
            validationSchema={partStepSchemas[currentFormStep]}
            enableReinitialize={true}
            onSubmit={handleSubmit}>
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, validateForm, handleSubmit, setTouched }) => {

                const handleNext = async () => {
                    const allErrors = await validateForm();
                    const stepFields = Object.keys((partStepSchemas[currentFormStep] && partStepSchemas[currentFormStep].fields) || {});
                    const stepHasError = stepFields.some((f) => allErrors && Object.prototype.hasOwnProperty.call(allErrors, f));
                    if (stepHasError) {
                        const touchedObj = {};
                        stepFields.forEach((f) => { touchedObj[f] = true; });
                        setTouched(touchedObj);
                        return;
                    }
                    setCurrentFormStep((s) => s + 1);
                };

                const handlePrevious = () => setCurrentFormStep((s) => Math.max(0, s - 1));

                return (
                    <>
                        <Breadcrumbs values={['Parts', 'Add New Part']} />

                        <div className='page_header access_view'>
                            <h2>Add New Part</h2>

                            <div className='d-flex align-items-center gap-2'>
                                <Button cancelBtn={() => { navigate(-1) }} loading={partCreateLoading} disabled={partCreateLoading}
                                    submitText={partCreateLoading ? "Saving..." : "Save"} type="button" shouldRow={false} isShowSubmitBtn={true} submitBtn={handleSubmit} />
                            </div>
                        </div>

                        <div className='page_container'>
                            <Row>
                                <Col md={9}>
                                    <div className='trip_information_main_div'>
                                        <h5>Part Information </h5>

                                        <div>
                                            <h6>Part ID: <span>{getPartId || '--'}</span></h6>

                                            <div className='d-flex'>
                                                <div style={{ minWidth: "175px" }}>
                                                    <Steps
                                                        className="form_steps_vertical"
                                                        current={currentFormStep}
                                                        onChange={async (v) => {
                                                            if (v > currentFormStep) {
                                                                const allErrors = await validateForm();
                                                                const stepFields = Object.keys((partStepSchemas[currentFormStep] && partStepSchemas[currentFormStep].fields) || {});
                                                                const stepHasError = stepFields.some((f) => allErrors && Object.prototype.hasOwnProperty.call(allErrors, f));
                                                                if (stepHasError) {
                                                                    const touchedObj = {};
                                                                    stepFields.forEach((f) => { touchedObj[f] = true; });
                                                                    setTouched(touchedObj);
                                                                    return;
                                                                }
                                                            }
                                                            setCurrentFormStep(v);
                                                        }}
                                                        direction="vertical"
                                                        items={[
                                                            { title: 'General Details' },
                                                            { title: 'Specifications' },
                                                            { title: 'Measurement' },
                                                            { title: 'Stocks' }
                                                        ]}
                                                    />
                                                </div>
                                                <div className='w-100 mb-3'>
                                                    {
                                                        currentFormStep === 0 &&
                                                        <Row>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Part Name"
                                                                    type="text"
                                                                    placeholder='Enter Part Name'
                                                                    name="part_name"
                                                                    value={values.part_name}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.part_name}
                                                                    touched={touched.part_name}
                                                                />
                                                            </Col>

                                                            <Col md={6}>
                                                                <Input
                                                                    label="Part Number"
                                                                    type="text"
                                                                    placeholder='Enter Part Number'
                                                                    name="part_number"
                                                                    value={values.part_number}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.part_number}
                                                                    touched={touched.part_number}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="SKU"
                                                                    type="text"
                                                                    placeholder='Enter SKU'
                                                                    name="sku"
                                                                    value={values.sku}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.sku}
                                                                    touched={touched.sku}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Select
                                                                    label="Category"
                                                                    placeholder="Select Category"
                                                                    options={partCategoriesDropdownData}
                                                                    loading={loading}
                                                                    value={(partCategoriesDropdownData?.find(o => o.value === values.category) || null)}
                                                                    name="category"
                                                                    onChange={(opt) => setFieldValue('category', opt ? opt.value : '')}
                                                                    onBlur={handleBlur}
                                                                    error={errors.category || error}
                                                                    touched={touched.category}
                                                                    isSearch={false}
                                                                />
                                                            </Col>

                                                            <Col md={6}>
                                                                <Input
                                                                    label="Company Name"
                                                                    type="text"
                                                                    placeholder='Enter Company Name'
                                                                    name="company_name"
                                                                    value={values.company_name}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.company_name}
                                                                    touched={touched.company_name}
                                                                />
                                                            </Col>

                                                            <Col md={12}>
                                                                <Input
                                                                    label="Description"
                                                                    placeholder="Enter part description (e.g., specifications, usage, dimensions, or any relevant details)"
                                                                    type="text"
                                                                    name="description"
                                                                    value={values.description}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.description}
                                                                    touched={touched.description}
                                                                    quill={true}
                                                                    setFieldValue={setFieldValue}
                                                                />
                                                            </Col>
                                                            <Col md={12}>
                                                                <div className='issue_file_upload_wrapper'>
                                                                    <label>Attachment</label>
                                                                    <FileUpload
                                                                        destination="service-entry"
                                                                        files={uploadedFiles}
                                                                        setFiles={setUploadedFiles}
                                                                        maxFileSize={10}
                                                                        maxFiles={1}
                                                                        accept="image/*"
                                                                        className='issue_file_upload'
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col md={12}>
                                                                <div className='d-flex align-items-center gap-2'>
                                                                    <Button cancelBtn={handlePrevious} loading={false} disabled={false}
                                                                        cancelBtnDisabled={currentFormStep === 0}
                                                                        submitText="Next" type="button" shouldRow={false} isShowSubmitBtn={true}
                                                                        submitBtn={handleNext} cancelBtnText="Previous"
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    }
                                                    {
                                                        currentFormStep === 1 &&
                                                        <Row>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Length (cm)"
                                                                    type="number"
                                                                    placeholder='00'
                                                                    name="length"
                                                                    value={values.length}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.length}
                                                                    touched={touched.length}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Width (cm)"
                                                                    type="number"
                                                                    placeholder='00'
                                                                    name="width"
                                                                    value={values.width}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.width}
                                                                    touched={touched.width}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Height (cm)"
                                                                    type="number"
                                                                    placeholder='00'
                                                                    name="height"
                                                                    value={values.height}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.height}
                                                                    touched={touched.height}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Weight (kg)"
                                                                    type="number"
                                                                    placeholder='00'
                                                                    name="weight"
                                                                    value={values.weight}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.weight}
                                                                    touched={touched.weight}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Select
                                                                    label="Material"
                                                                    placeholder="Select Material"
                                                                    options={partsMaterialDropdownData}
                                                                    value={partsMaterialDropdownData?.find(option => option.value === values.material)}
                                                                    loading={loading}
                                                                    name="material"
                                                                    onChange={(opt) => setFieldValue('material', opt ? opt.value : '')}
                                                                    onBlur={handleBlur}
                                                                    error={errors.material}
                                                                    touched={touched.material}
                                                                    isSearch={false}
                                                                />
                                                            </Col>
                                                            <Col md={12}>
                                                                <h6 style={{ background: "none", color: "#3D3F5C", padding: "0px", fontSize: "18px" }}>Compatibility</h6>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Select
                                                                    label="Make"
                                                                    placeholder="Select Make"
                                                                    options={partsMakesDropdownData}
                                                                    value={partsMakesDropdownData?.find(option => option.value === values.make)}
                                                                    loading={loading}
                                                                    name="make"
                                                                    onChange={(opt) => setFieldValue('make', opt ? opt.value : '')}
                                                                    onBlur={handleBlur}
                                                                    error={errors.make}
                                                                    touched={touched.make}
                                                                    isSearch={false}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Select
                                                                    label="Model"
                                                                    placeholder="Select Model"
                                                                    options={partsModelDropdownData}
                                                                    value={partsModelDropdownData?.find(option => option.value === values.model)}
                                                                    loading={loading}
                                                                    name="model"
                                                                    onChange={(opt) => setFieldValue('model', opt ? opt.value : '')}
                                                                    onBlur={handleBlur}
                                                                    error={errors.model}
                                                                    touched={touched.model}
                                                                    isSearch={false}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Year"
                                                                    type="date-year"
                                                                    name="year"
                                                                    value={values.year}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.year}
                                                                    touched={touched.year}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Engine Code"
                                                                    type="text"
                                                                    placeholder='00'
                                                                    name="engineCode"
                                                                    value={values.engineCode}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.engineCode}
                                                                    touched={touched.engineCode}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Cross Reference SKU"
                                                                    type="text"
                                                                    placeholder='00'
                                                                    name="crossReferenceSku"
                                                                    value={values.crossReferenceSku}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.crossReferenceSku}
                                                                    touched={touched.crossReferenceSku}
                                                                />
                                                            </Col>
                                                            <Col md={12}>
                                                                <h6 style={{ background: "none", color: "#3D3F5C", padding: "0px", fontSize: "18px" }}>Constraints</h6>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Max Pressure (psi)"
                                                                    type="text"
                                                                    placeholder='00'
                                                                    name="maxPressure"
                                                                    value={values.maxPressure}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.maxPressure}
                                                                    touched={touched.maxPressure}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Max Voltage (V)"
                                                                    type="text"
                                                                    placeholder='00'
                                                                    name="maxVoltage"
                                                                    value={values.maxVoltage}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.maxVoltage}
                                                                    touched={touched.maxVoltage}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Max Temperature (C)"
                                                                    type="text"
                                                                    placeholder='00'
                                                                    name="maxTemperature"
                                                                    value={values.maxTemperature}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.maxTemperature}
                                                                    touched={touched.maxTemperature}
                                                                />
                                                            </Col>
                                                            <Col md={12}>
                                                                <div className='d-flex align-items-center gap-2'>
                                                                    <Button cancelBtn={handlePrevious} loading={false} disabled={false}
                                                                        cancelBtnDisabled={currentFormStep === 0}
                                                                        submitText="Next" type="button" shouldRow={false} isShowSubmitBtn={true}
                                                                        submitBtn={handleNext} cancelBtnText="Previous"
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    }
                                                    {
                                                        currentFormStep === 2 &&
                                                        <Row>
                                                            <Col md={6}>
                                                                <Select
                                                                    label="Unit of Measurement"
                                                                    placeholder="Select Unit of Measurement"
                                                                    options={partsUnitsDropdownData}
                                                                    value={partsUnitsDropdownData?.find(option => option.value === values.unitOfMeasurement)}
                                                                    loading={loading}
                                                                    name="unitOfMeasurement"
                                                                    onChange={(opt) => setFieldValue('unitOfMeasurement', opt ? opt.value : '')}
                                                                    onBlur={handleBlur}
                                                                    error={errors.unitOfMeasurement}
                                                                    touched={touched.unitOfMeasurement}
                                                                    isSearch={false}
                                                                />
                                                            </Col>

                                                            <Col md={12}>
                                                                <div className='d-flex align-items-center gap-2'>
                                                                    <Button cancelBtn={handlePrevious} loading={false} disabled={false}
                                                                        cancelBtnDisabled={currentFormStep === 0}
                                                                        submitText="Next" type="button" shouldRow={false} isShowSubmitBtn={true}
                                                                        submitBtn={handleNext} cancelBtnText="Previous"
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    }
                                                    {
                                                        currentFormStep === 3 &&
                                                        <Row>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Safety Stock Threshold"
                                                                    type="number"
                                                                    placeholder='00'
                                                                    name="safetyStockThreshold"
                                                                    value={values.safetyStockThreshold}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.safetyStockThreshold}
                                                                    touched={touched.safetyStockThreshold}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Available Stock"
                                                                    type="number"
                                                                    placeholder='00'
                                                                    name="availableStock"
                                                                    value={values.availableStock}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.availableStock}
                                                                    touched={touched.availableStock}
                                                                />
                                                            </Col>
                                                            <Col md={6}>
                                                                <Input
                                                                    label="Unit Price"
                                                                    type="number"
                                                                    step="0.01"
                                                                    placeholder='0.00'
                                                                    name="price"
                                                                    value={values.price}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.price}
                                                                    touched={touched.price}
                                                                />
                                                            </Col>
                                                            {/* <Col md={6}>
                                                                <Select
                                                                    label="Vendor"
                                                                    placeholder="Select Vendor"
                                                                    options={vendorsDropdownData}
                                                                    value={vendorsDropdownData?.find(option => option.value === values.vendor)}
                                                                    loading={loading}
                                                                    name="vendor"
                                                                    onChange={(opt) => setFieldValue('vendor', opt ? opt.value : '')}
                                                                    onBlur={handleBlur}
                                                                    error={errors.vendor}
                                                                    touched={touched.vendor}
                                                                    isSearch={false}
                                                                />
                                                            </Col> */}
                                                            <Col md={12}>
                                                                <div className='d-flex align-items-center gap-2'>
                                                                    <Button cancelBtn={handlePrevious} loading={false} disabled={false}
                                                                        cancelBtnDisabled={currentFormStep === 0}
                                                                        submitText="Save" type="button" shouldRow={false} isShowSubmitBtn={false}
                                                                        submitBtn={handleSubmit} cancelBtnText="Previous"
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className='trip_notes_main_div'>
                                        <h5>Notes</h5>

                                        <ReactQuill
                                            placeholder={'Write notes here...'}
                                            modules={modules}
                                            theme="snow"
                                            value={values.notes || ''}
                                            formats={formats}
                                            onChange={(content) => setFieldValue('notes', content)}
                                            className='react_quill_input notes_quill_input quill-small-font trip_quill_input'
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </>
                )
            }}
        </Formik>
    )
}

export default PartsCreate
